import { useCallback, useEffect, useRef, useState } from "react";

export type LiveSnapshot = {
  walletId: string;
  ts_ms: number;
  perps: {
    accountValue: number | null;
    marginUsed: number | null;
    unrealizedPnl: number | null;
    state: unknown;
  };
  spot: {
    equity: number | null;
    state: unknown;
  };
};

interface UseLiveSnapshotReturn {
  data: LiveSnapshot | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useLiveSnapshot(address: string | undefined, enabled: boolean = true): UseLiveSnapshotReturn {
  const [data, setData] = useState<LiveSnapshot | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timerRef = useRef<number | null>(null);

  const refresh = useCallback(async () => {
    if (!address || !enabled) return;

    setIsLoading(true);
    setError(null);

    try {
      const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
      const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

      const res = await fetch(
        `${SUPABASE_URL}/functions/v1/hl-live-snapshot?address=${encodeURIComponent(address)}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            apikey: SUPABASE_PUBLISHABLE_KEY,
            Authorization: `Bearer ${SUPABASE_PUBLISHABLE_KEY}`,
          },
        }
      );

      if (!res.ok) {
        let message = "Failed to fetch live snapshot";
        try {
          const j = await res.json();
          message = j.error || message;
        } catch {
          try {
            const t = await res.text();
            message = t || message;
          } catch {
            // ignore
          }
        }
        throw new Error(`${message} (HTTP ${res.status})`);
      }

      const json = (await res.json()) as LiveSnapshot;
      setData(json);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to fetch live snapshot";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [address, enabled]);

  useEffect(() => {
    if (!address || !enabled) {
      if (timerRef.current != null) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    refresh();

    timerRef.current = window.setInterval(() => {
      refresh();
    }, 3000);

    return () => {
      if (timerRef.current != null) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [address, enabled, refresh]);

  return { data, isLoading, error, refresh };
}
